import { useMemo } from "react";
import { useLingui } from "@lingui/macro";

import type { PathwayDimension, Representation } from "@app/types/dashboard-pathway";
import type { PathwayCard } from "@app/zustand/dashboard-pathway";

/**
 * Labels and presets that several cards share.
 *
 * In a file of their own rather than beside the components that use them: a module exporting both a
 * component and a plain function loses fast refresh, so an edit to a card reloads the page instead
 * of the card.
 */

export function useRepresentationLabels(): Record<Representation, string> {
  const { t } = useLingui();
  return {
    empilement: t`Empilement`,
    matrice: t`Matrice`,
    regle: t`Règle d'intervalles`,
    emboitement: t`Emboîtement`,
    flux: t`Flux`,
    aires: t`Aires empilées`,
    courbe: t`Courbe de volume`,
    distribution: t`Distribution`,
    tableau: t`Tableau compté`,
  };
}

export interface QuestionPreset {
  id: string;
  label: string;
  target: "composition" | "singularite";
  patch: Partial<PathwayCard>;
}

/**
 * The preset questions, derived from what the ontology declares and from nothing else.
 *
 * A question is not a card: it is a preset that shows its own mechanics. Clicking one reconfigures a
 * card and its dimension chips change under the reader's eyes, so after three questions they know
 * they can remove a chip themselves and no longer need the bar. A menu of hard-written questions is
 * a menu you cannot get out of.
 *
 * Nothing here names a field. Each preset is expressed in terms the ontology owns — the act
 * (`temporal_role = current`), the interval axis, the episode's own outcome — so a preset simply
 * does not appear on a speciality that declares none of it.
 */
export function useQuestionPresets(
  dimensions: PathwayDimension[],
  scope: "mine" | "specialite" = "mine",
  /** False for a reader who is not a practitioner of the source — the "vue globale" persona. */
  hasPractitioner = true,
): QuestionPreset[] {
  const { t } = useLingui();
  return useMemo(() => {
    // "Exists" is not enough: a dimension the ontology declares and no episode documents draws an
    // empty card, and a preset that does that is worse than a missing preset. Native axes included:
    // no knee arthroplasty has an outcome, and no gynaecology act a delay before it.
    const usable = (key: string) =>
      dimensions.some((d) => d.key === key && d.documented > 0);
    const has = usable;
    const actDimension = dimensions.find(
      (d) => !d.native && d.role === "current" && d.documented > 0,
    );
    // The act holds its class (« Discectomie », whatever the report wrote): a question about what is
    // done counts kinds of act.
    const act = actDimension?.key;
    // A grammar that is not surgical words its own questions; the defaults below are the surgeon's.
    const asked = (key: string) =>
      dimensions.find((d) => d.key === key)?.question ?? null;
    const interval = dimensions.find((d) => d.kind === "intervalle" && d.documented > 0)?.key;
    const presets: QuestionPreset[] = [];

    /* The perimeter changes the QUESTION, not only its wording. In "my patientèle" the subject is
       the reader: what do I operate on, what sets me apart. In "the whole speciality" the reader is
       one practitioner among several, so the questions that only that perimeter can answer come
       first — who does what, and where do I fall in the spread. Asking "what do I operate on" on a
       board that shows everyone's episodes is a question the page cannot answer. */
    const speciality = scope === "specialite";

    if (act && has("indication")) {
      presets.push({
        id: "operations",
        label:
          actDimension?.question ??
          (speciality || !hasPractitioner
            ? t`Que fait la spécialité ?`
            : t`Qu'est-ce que j'opère ?`),
        target: "composition",
        patch: { dims: ["indication", act], mode: "n", representation: null },
      });
    }
    if (act && interval) {
      presets.push({
        id: "levels",
        label: t`À quels niveaux ?`,
        target: "composition",
        patch: { dims: [act, interval], mode: "n", representation: null },
      });
    }
    if (has("indication") && has("outcome")) {
      presets.push({
        id: "outcomes",
        label: t`Comment ça se termine ?`,
        target: "composition",
        patch: { dims: ["indication", "outcome"], mode: "n", representation: null },
      });
    }
    // Only this layer can answer it: it needs the indication and the act to be RELATED, which is
    // precisely the relation the flat index never wrote down.
    if (has("delai_intervention")) {
      presets.push({
        id: "delay",
        label:
          asked("delai_intervention") ?? t`Combien de temps avant d'opérer ?`,
        target: "composition",
        patch: { dims: ["delai_intervention"], mode: "n", representation: null },
      });
    }
    // Over the years, what was decided — or, for a pathway that decides nothing, what the act was.
    const trend = has("decision") ? "decision" : act;
    if (trend && has("annee")) {
      presets.push({
        id: "over-time",
        label: t`Comment ça évolue ?`,
        target: "composition",
        patch: { dims: ["annee", trend], mode: "n", representation: null },
      });
    }
    /* The comparison questions are the SAME in both perimeters, and that is a correction.
       "Où suis-je atypique ?" used to exist only in the speciality tab while "Qu'est-ce qui me
       distingue ?" existed only in the other, so switching tab made a chip vanish and another
       appear — two labels for one card, and a board that looks broken on a control the reader just
       touched. One question, one label, always present; only the CARD changes underneath, showing
       the gap in one perimeter and the spread in the other, which is the whole design.

       What genuinely differs is whether there is a "me" at all. */
    if (!hasPractitioner) {
      presets.push({
        id: "distinctive",
        label: t`Qu'est-ce qui varie le plus ?`,
        target: "singularite",
        patch: { family: null },
      });
    } else {
      presets.push({
        id: "recruitment",
        label: t`Qui sont mes patients ?`,
        target: "singularite",
        patch: { family: "patient" },
      });
      presets.push({
        id: "distinctive",
        label: t`Qu'est-ce qui me distingue ?`,
        target: "singularite",
        patch: { family: null },
      });
    }
    return presets;
  }, [dimensions, scope, hasPractitioner, t]);
}
